/**
 * Sector operations — Stays PMS connector framework.
 * R39 K12.a + K12.b (plan docs/plans/sector-operations.md SO-D201, SO-D207, SO-D212, SO-P201).
 *
 * Generic PMS (Timelining format) adapter, edge reduction with zero guest data,
 * HMAC-SHA256 signature verification, room mapping, and write-back outbox.
 */

import type { OpsRoomAction } from './roomStates.js'

export const PMS_EVENT_TYPES = [
  'CHECKED_OUT',
  'STAYOVER',
  'DUE_OUT',
  'OUT_OF_ORDER',
  'BACK_IN_SERVICE',
  'ROOM_MOVE',
] as const

export type PmsEventType = (typeof PMS_EVENT_TYPES)[number]

export const PMS_ADAPTER_TYPES = [
  'GENERIC',
  'EZEE',
  'HOTELOGIX',
  'STAYFLEXI',
  'DJUBO',
  'IDS_NEXT',
] as const

export type PmsAdapterType = (typeof PMS_ADAPTER_TYPES)[number]

/**
 * Edge-reduced PMS event. Strictly allowlisted fields only.
 * Guest data (names, phones, IDs, booking IDs, amounts) MUST be discarded at the edge before storage or logging.
 */
export interface ReducedPmsEvent {
  eventId: string
  propertyCode: string
  roomNumber: string
  type: PmsEventType
  at: number
  toRoomNumber?: string
}

/**
 * Reduce a raw inbound PMS payload to the sanitized allowlisted event fields.
 * Any extra fields (guestName, phone, id, bookingId, notes, amount, etc.) are stripped.
 */
export function reduceGenericPmsEvent(raw: unknown): ReducedPmsEvent {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid PMS event payload: payload must be an object.')
  }

  const rec = raw as Record<string, unknown>

  const eventId = typeof rec.eventId === 'string' ? rec.eventId.trim() : ''
  if (!eventId || eventId.length < 1 || eventId.length > 100) {
    throw new Error('Invalid PMS event payload: eventId must be between 1 and 100 characters.')
  }

  const propertyCode = typeof rec.propertyCode === 'string' ? rec.propertyCode.trim() : ''
  if (!propertyCode || propertyCode.length > 64) {
    throw new Error('Invalid PMS event payload: propertyCode must be between 1 and 64 characters.')
  }

  const roomNumber = typeof rec.roomNumber === 'string'
    ? rec.roomNumber.trim()
    : typeof rec.roomNumber === 'number'
      ? String(rec.roomNumber)
      : ''
  if (!roomNumber || roomNumber.length > 64) {
    throw new Error('Invalid PMS event payload: roomNumber must be between 1 and 64 characters.')
  }

  const rawType = typeof rec.type === 'string' ? rec.type.trim().toUpperCase() : ''
  if (!PMS_EVENT_TYPES.includes(rawType as PmsEventType)) {
    throw new Error(`Invalid PMS event payload: unsupported type "${rawType}".`)
  }
  const type = rawType as PmsEventType

  let at: number
  if (typeof rec.at === 'number' && Number.isFinite(rec.at)) {
    at = rec.at
  } else if (typeof rec.at === 'string') {
    const parsed = Date.parse(rec.at)
    if (isNaN(parsed)) {
      throw new Error(`Invalid PMS event payload: invalid timestamp "${rec.at}".`)
    }
    at = parsed
  } else {
    throw new Error('Invalid PMS event payload: timestamp "at" is required.')
  }

  let toRoomNumber: string | undefined
  if (type === 'ROOM_MOVE') {
    const rawTo = typeof rec.toRoomNumber === 'string'
      ? rec.toRoomNumber.trim()
      : typeof rec.toRoomNumber === 'number'
        ? String(rec.toRoomNumber)
        : ''
    if (!rawTo) {
      throw new Error('Invalid PMS event payload: toRoomNumber is required for ROOM_MOVE events.')
    }
    if (rawTo.length > 64) {
      throw new Error('Invalid PMS event payload: toRoomNumber must be between 1 and 64 characters for ROOM_MOVE events.')
    }
    toRoomNumber = rawTo
  }

  return {
    eventId,
    propertyCode,
    roomNumber,
    type,
    at,
    ...(toRoomNumber ? { toRoomNumber } : {}),
  }
}

export type MappedPmsAction =
  | { kind: 'SINGLE'; action: OpsRoomAction }
  | { kind: 'ROOM_MOVE'; fromAction: OpsRoomAction; toAction: OpsRoomAction; toRoomNumber: string }

/**
 * Map a reduced PMS event to its corresponding room state transition action(s).
 */
export function mapPmsEventToRoomAction(event: ReducedPmsEvent): MappedPmsAction {
  switch (event.type) {
    case 'CHECKED_OUT':
      return { kind: 'SINGLE', action: { type: 'CHECK_OUT' } }

    case 'STAYOVER':
      return { kind: 'SINGLE', action: { type: 'MARK_OCCUPIED' } }

    case 'DUE_OUT':
      return { kind: 'SINGLE', action: { type: 'SET_DUE_OUT', dueOutAt: event.at } }

    case 'OUT_OF_ORDER':
      return {
        kind: 'SINGLE',
        action: {
          type: 'SET_SERVICE',
          service: 'OUT_OF_ORDER',
          serviceReason: 'PMS',
        },
      }

    case 'BACK_IN_SERVICE':
      return { kind: 'SINGLE', action: { type: 'BACK_IN_SERVICE' } }

    case 'ROOM_MOVE':
      return {
        kind: 'ROOM_MOVE',
        fromAction: { type: 'CHECK_OUT' },
        toAction: { type: 'MARK_OCCUPIED' },
        toRoomNumber: event.toRoomNumber!,
      }
  }
}

// --- Outbox Write-back Queue ---

export const PMS_OUTBOX_TYPES = ['READY', 'OUT_OF_ORDER', 'IN_SERVICE'] as const
export type OpsPmsOutboxType = (typeof PMS_OUTBOX_TYPES)[number]

export const PMS_OUTBOX_STATES = ['PENDING', 'RETRY', 'PROCESSING', 'SYNCED', 'FAILED'] as const
export type OpsPmsOutboxState = (typeof PMS_OUTBOX_STATES)[number]

export const PMS_OUTBOX_BACKOFF_MINUTES = [1, 5, 15, 60, 240] as const

export interface OpsPmsOutboxItem {
  id: string
  workspaceId: string
  propertyId: string
  roomId: string
  roomNumber: string
  type: OpsPmsOutboxType
  at: number
  attempts: number
  state: OpsPmsOutboxState
  nextAttemptAt?: number | null
  leaseUntil?: number | null
  lastAttemptAt?: number | null
  lastError?: string | null
  syncedAt?: number | null
  createdAt?: unknown
  updatedAt?: unknown
}

export function computePmsOutboxBackoff(
  attempts: number,
  now = Date.now(),
): { state: 'RETRY' | 'FAILED'; nextAttemptAt?: number } {
  if (attempts >= 5) {
    return { state: 'FAILED' }
  }
  const delayMinutes = PMS_OUTBOX_BACKOFF_MINUTES[Math.min(attempts - 1, PMS_OUTBOX_BACKOFF_MINUTES.length - 1)] ?? 240
  return {
    state: 'RETRY',
    nextAttemptAt: now + delayMinutes * 60_000,
  }
}

// --- Room State PMS Sync Flag ---

export interface OpsRoomPmsSync {
  state: 'SYNCED' | 'PENDING' | 'FAILED'
  at: number
}

/**
 * Determine if a room should display the "Not synced to PMS" chip on the room board.
 * Shows for FAILED, and PENDING older than 15 minutes.
 */
export function isRoomNotSyncedToPms(pmsSync?: OpsRoomPmsSync | null, now = Date.now()): boolean {
  if (!pmsSync) return false
  if (pmsSync.state === 'FAILED') return true
  if (pmsSync.state === 'PENDING' && now - pmsSync.at > 15 * 60_000) return true
  return false
}

// --- Connection Metadata Types ---

export interface StaysPmsWriteBackConfig {
  enabled: boolean
  callbackUrl?: string | null
}

export interface StaysPmsConnectionMetadata {
  workspaceId: string
  providerId: 'STAYS_PMS'
  adapter: PmsAdapterType
  status: 'ACTIVE' | 'DISABLED'
  lastEventAt?: number | null
  lastPollAt?: number | null
  lastImportAt?: number | null
  lastErrorCode?: string | null
  pollLeaseUntil?: number | null
  importLeaseUntil?: number | null
  reportColumns?: Record<string, string>
  writeBack: StaysPmsWriteBackConfig
  propertyMap: Record<string, string> // pmsPropertyCode -> timelining propertyId
  tokenRef?: string
  createdAt?: unknown
  updatedAt?: unknown
}

// ---------------------------------------------------------------------------
// Polling Adapter Edge Reduction & Snapshot Diffing (SO-P126, SO-P127)
// ---------------------------------------------------------------------------

export interface ReducedPmsRoomRecord {
  roomName: string
  roomId: string
  unitId?: string
  roomTypeId?: string
  isBlocked: boolean
  hkStatus?: string
  roomStatus?: string
  inHouseStatus: string | null
  resvKey: string | null
  dnrId?: string | null
}

export type ReducedEzeeRoomRecord = ReducedPmsRoomRecord
export type ReducedHotelogixRoomRecord = ReducedPmsRoomRecord

/**
 * Reduce a raw eZee Inhouse Room Status response at the edge to allowlisted per-room records.
 * STRICTLY DROPS all guest personal data (names, phones, emails, addresses, company, rates,
 * and raw reservation numbers). resvKey is computed using the supplied HMAC function.
 */
export function reduceEzeeInhouse(
  raw: unknown,
  resvKeyFn: (reservationNo: string) => string,
): Record<string, ReducedEzeeRoomRecord> {
  if (!raw || typeof raw !== 'object') {
    return {}
  }

  const rec = raw as Record<string, unknown>
  const dataRec = rec.data && typeof rec.data === 'object' ? (rec.data as Record<string, unknown>) : null
  const respRec = rec.response && typeof rec.response === 'object' ? (rec.response as Record<string, unknown>) : null

  const rawRoomList = Array.isArray(rec.roomlist)
    ? rec.roomlist
    : Array.isArray(dataRec?.roomlist)
      ? (dataRec?.roomlist as unknown[])
      : Array.isArray(respRec?.roomlist)
        ? (respRec?.roomlist as unknown[])
        : []

  const rawGuestList = Array.isArray(rec.checkinguestlist)
    ? rec.checkinguestlist
    : Array.isArray(dataRec?.checkinguestlist)
      ? (dataRec?.checkinguestlist as unknown[])
      : Array.isArray(respRec?.checkinguestlist)
        ? (respRec?.checkinguestlist as unknown[])
        : []

  // Build guest mapping by room (room name or room id)
  interface GuestInfo {
    inHouseStatus: string | null
    resvKey: string | null
  }
  const guestByRoom = new Map<string, GuestInfo>()

  for (const g of rawGuestList) {
    if (!g || typeof g !== 'object') continue
    const gObj = g as Record<string, unknown>
    const rawRoom = String(gObj.room ?? gObj.roomname ?? gObj.roomid ?? '').trim().toLowerCase()
    if (!rawRoom) continue

    const resvNo = typeof gObj.reservationno === 'string'
      ? gObj.reservationno.trim()
      : typeof gObj.reservationno === 'number'
        ? String(gObj.reservationno)
        : ''

    const inHouseStatus = typeof gObj.bookingstatus === 'string' && gObj.bookingstatus.trim()
      ? gObj.bookingstatus.trim()
      : typeof gObj.status === 'string' && gObj.status.trim()
        ? gObj.status.trim()
        : null

    const resvKey = resvNo ? resvKeyFn(resvNo) : null

    guestByRoom.set(rawRoom, {
      inHouseStatus,
      resvKey,
    })
  }

  const result: Record<string, ReducedEzeeRoomRecord> = {}

  for (const r of rawRoomList) {
    if (!r || typeof r !== 'object') continue
    const rObj = r as Record<string, unknown>

    const roomName = String(rObj.roomname ?? rObj.room_name ?? rObj.roomid ?? '').trim()
    if (!roomName) continue

    const roomId = String(rObj.roomid ?? rObj.room_id ?? roomName).trim()
    const unitId = String(rObj.unitid ?? rObj.unit_id ?? '').trim()
    const roomTypeId = String(rObj.roomtypeid ?? rObj.room_type_id ?? '').trim()

    const rawBlocked = rObj.isblocked ?? rObj.is_blocked
    const isBlocked =
      rawBlocked === true ||
      rawBlocked === 1 ||
      rawBlocked === '1' ||
      (typeof rawBlocked === 'string' && /^(yes|y|true)$/i.test(rawBlocked.trim()))

    const hkStatus = String(rObj.hkstatus ?? rObj.hk_status ?? '').trim()
    const roomStatus = String(rObj.roomstatus ?? rObj.room_status ?? '').trim()

    // Link guest
    const guest =
      guestByRoom.get(roomName.toLowerCase()) ||
      guestByRoom.get(roomId.toLowerCase())

    result[roomName] = {
      roomName,
      roomId,
      unitId,
      roomTypeId,
      isBlocked,
      hkStatus,
      roomStatus,
      inHouseStatus: guest?.inHouseStatus ?? null,
      resvKey: guest?.resvKey ?? null,
    }
  }

  return result
}

function fnv1a64(str: string): string {
  let h1 = 0x811c9dc5
  let h2 = 0xcbf29ce4
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 0x01000193)
    h2 = Math.imul(h2 ^ (ch << 1), 0x01000193)
  }
  const hex1 = (h1 >>> 0).toString(16).padStart(8, '0')
  const hex2 = (h2 >>> 0).toString(16).padStart(8, '0')
  return `${hex1}${hex2}`
}

export function deterministicPmsEventId(
  connectionId: string,
  propertyCode: string,
  roomNumber: string,
  type: string,
  transition: string,
  hotelDate = '',
  prefix = 'ezee',
): string {
  const seed = [connectionId, propertyCode, roomNumber, type, transition, hotelDate].join(':')
  return `${prefix}_${fnv1a64(seed)}`
}

export interface DiffPmsRoomSnapshotsOptions {
  connectionId: string
  propertyCode: string
  hotelDate: string
  at?: number
  prefix?: string
}

export type DiffEzeeSnapshotsOptions = DiffPmsRoomSnapshotsOptions

export interface EzeeClassifiedResponse {
  isError: boolean
  isAuth: boolean
  errorCode: 'EZEE_AUTH' | 'POLL_ERROR' | null
  message: string
}

/**
 * Classifies an eZee response body (per SO-P126 and R39 K13.a fix1).
 * Error codes can be string or number (e.g. "1" or 1).
 * Unauthorized / inactive auth keywords map to EZEE_AUTH.
 * Any other non-zero errorcode / ErrorCode maps to POLL_ERROR.
 * Empty or missing roomlist maps to POLL_ERROR when requireRoomlist is set.
 */
export function classifyEzeeResponse(
  rawJson: unknown,
  options?: { requireRoomlist?: boolean },
): EzeeClassifiedResponse {
  if (!rawJson || typeof rawJson !== 'object') {
    return {
      isError: true,
      isAuth: false,
      errorCode: 'POLL_ERROR',
      message: 'Invalid or non-object response from eZee API',
    }
  }

  const rec = rawJson as Record<string, unknown>
  const rawCode = rec.errorcode ?? rec.ErrorCode ?? rec.code
  const rawMsg =
    rec.message ??
    rec.ErrorMessage ??
    rec.msg ??
    rec.error ??
    rec.errorMessage ??
    ''
  const msgText = String(rawMsg).trim()

  // 1. Check for auth keywords in message
  const isAuthMsg = /(unauthorized|auth code is inactive|not authorized|deactivated)/i.test(msgText)

  // 2. Parse code as number (supports numeric strings like "1", "301", etc.)
  let numCode: number | null = null
  if (typeof rawCode === 'number') {
    numCode = rawCode
  } else if (typeof rawCode === 'string' && rawCode.trim() !== '') {
    const parsed = Number(rawCode)
    if (!Number.isNaN(parsed)) {
      numCode = parsed
    }
  }

  // 3. Known auth error codes
  const isAuthCode = numCode === 301 || numCode === 303 || rawCode === '301' || rawCode === '303'

  if (isAuthMsg || isAuthCode) {
    return {
      isError: true,
      isAuth: true,
      errorCode: 'EZEE_AUTH',
      message: msgText || `eZee authentication failure (code ${rawCode ?? 'unknown'})`,
    }
  }

  // 4. Non-zero code is an error
  if (numCode !== null && numCode !== 0) {
    return {
      isError: true,
      isAuth: false,
      errorCode: 'POLL_ERROR',
      message: msgText || `eZee error code ${rawCode}`,
    }
  }

  // If there's an error string present without a code
  if (typeof rec.error === 'string' && rec.error.trim()) {
    return {
      isError: true,
      isAuth: false,
      errorCode: 'POLL_ERROR',
      message: rec.error.trim(),
    }
  }

  // 5. If roomlist is required (polling & test connection)
  if (options?.requireRoomlist) {
    const roomlist = Array.isArray(rec.roomlist)
      ? rec.roomlist
      : rec.data && typeof rec.data === 'object' && Array.isArray((rec.data as Record<string, unknown>).roomlist)
        ? (rec.data as Record<string, unknown>).roomlist
        : null

    if (!roomlist || (roomlist as unknown[]).length === 0) {
      return {
        isError: true,
        isAuth: false,
        errorCode: 'POLL_ERROR',
        message: msgText || 'eZee response missing roomlist or roomlist is empty',
      }
    }
  }

  return {
    isError: false,
    isAuth: false,
    errorCode: null,
    message: '',
  }
}

/**
 * Pure adapter-agnostic diff between previous and next room snapshots per SO-P127.
 * Applies to eZee, Hotelogix, and future polling adapters.
 * Rules:
 * 1. First poll (prev is null/empty): baseline = no events.
 * 2. isblocked No->Yes OUT_OF_ORDER, Yes->No BACK_IN_SERVICE.
 * 3. in-house status becomes Due Out -> DUE_OUT.
 * 4. in-house status becomes Stayover -> STAYOVER (once per room per hotel date).
 * 5. resvKey in-house in room A then in room B -> ROOM_MOVE A->B.
 * 6. resvKey in-house in a room then Check Out or gone without a move -> CHECKED_OUT.
 * Event IDs are deterministic.
 */
export function diffPmsRoomSnapshots(
  prev: Record<string, ReducedPmsRoomRecord> | null | undefined,
  next: Record<string, ReducedPmsRoomRecord>,
  options: DiffPmsRoomSnapshotsOptions,
): ReducedPmsEvent[] {
  // Baseline check: first poll emits no events
  if (!prev || Object.keys(prev).length === 0) {
    return []
  }

  const { connectionId, propertyCode, hotelDate } = options
  const prefix = options.prefix || 'pms'
  const at = typeof options.at === 'number' ? options.at : Date.now()
  const events: ReducedPmsEvent[] = []

  // 1. Detect room moves and checkouts by tracking in-house resvKey transitions
  // Map of resvKey -> new room in next snapshot
  const nextResvKeyToRoom = new Map<string, string>()
  for (const [rName, rRec] of Object.entries(next)) {
    if (rRec.resvKey) {
      nextResvKeyToRoom.set(rRec.resvKey, rName)
    }
  }

  const movedResvKeys = new Set<string>()
  const movedToRooms = new Set<string>()

  for (const [prevRoomName, prevRec] of Object.entries(prev)) {
    if (!prevRec.resvKey) continue

    // A room whose prev status was already Check Out is not in-house: no repeat CHECKED_OUT on later polls or later hotel dates
    const prevWasCheckOut =
      typeof prevRec.inHouseStatus === 'string' &&
      /^(check\s*out|checked\s*out)$/i.test(prevRec.inHouseStatus.trim())
    if (prevWasCheckOut) {
      continue
    }

    // A room absent from next must emit nothing (it is dropped from snapshot comparison, not checked out)
    const nextRec = next[prevRoomName]
    if (!nextRec) {
      continue
    }

    const targetRoomInNext = nextResvKeyToRoom.get(prevRec.resvKey)
    if (targetRoomInNext && targetRoomInNext !== prevRoomName) {
      // Room Move: resvKey moved from prevRoomName to targetRoomInNext
      movedResvKeys.add(prevRec.resvKey)
      movedToRooms.add(targetRoomInNext)
      const type: PmsEventType = 'ROOM_MOVE'
      const transition = `MOVE:${prevRoomName}->${targetRoomInNext}`
      const eventId = deterministicPmsEventId(
        connectionId,
        propertyCode,
        prevRoomName,
        type,
        transition,
        hotelDate,
        prefix,
      )
      events.push({
        eventId,
        propertyCode,
        roomNumber: prevRoomName,
        toRoomNumber: targetRoomInNext,
        type,
        at,
      })
    } else {
      // CHECKED_OUT fires only when the room is present in next and either:
      // 1. has no resvKey
      // 2. has a different resvKey (with no move found)
      // 3. its status changed INTO Check Out
      const nextIsCheckOut =
        typeof nextRec.inHouseStatus === 'string' &&
        /^(check\s*out|checked\s*out)$/i.test(nextRec.inHouseStatus.trim())

      const hasNoResvKey = !nextRec.resvKey
      const hasDifferentResvKey = nextRec.resvKey !== prevRec.resvKey

      if (hasNoResvKey || hasDifferentResvKey || nextIsCheckOut) {
        const type: PmsEventType = 'CHECKED_OUT'
        const transition = `CHECKED_OUT:${prevRec.resvKey}`
        const eventId = deterministicPmsEventId(
          connectionId,
          propertyCode,
          prevRoomName,
          type,
          transition,
          hotelDate,
          prefix,
        )
        events.push({
          eventId,
          propertyCode,
          roomNumber: prevRoomName,
          type,
          at,
        })
      }
    }
  }

  // 2. Room-level state transitions (OOO, Back in service, Due out, Stayover)
  for (const [roomName, nextRec] of Object.entries(next)) {
    const prevRec = prev[roomName]
    if (!prevRec) continue

    // Blocked (Out of order / Back in service)
    if (!prevRec.isBlocked && nextRec.isBlocked) {
      const type: PmsEventType = 'OUT_OF_ORDER'
      const transition = 'isBlocked:false->true'
      const eventId = deterministicPmsEventId(
        connectionId,
        propertyCode,
        roomName,
        type,
        transition,
        hotelDate,
        prefix,
      )
      events.push({
        eventId,
        propertyCode,
        roomNumber: roomName,
        type,
        at,
      })
    } else if (prevRec.isBlocked && !nextRec.isBlocked) {
      const type: PmsEventType = 'BACK_IN_SERVICE'
      const transition = 'isBlocked:true->false'
      const eventId = deterministicPmsEventId(
        connectionId,
        propertyCode,
        roomName,
        type,
        transition,
        hotelDate,
        prefix,
      )
      events.push({
        eventId,
        propertyCode,
        roomNumber: roomName,
        type,
        at,
      })
    }

    // Due Out: in-house status becomes Due Out
    const prevDueOut =
      typeof prevRec.inHouseStatus === 'string' &&
      /^due\s*out$/i.test(prevRec.inHouseStatus.trim())
    const nextDueOut =
      typeof nextRec.inHouseStatus === 'string' &&
      /^due\s*out$/i.test(nextRec.inHouseStatus.trim())

    if (!prevDueOut && nextDueOut) {
      const type: PmsEventType = 'DUE_OUT'
      const transition = 'inHouseStatus:->DueOut'
      const eventId = deterministicPmsEventId(
        connectionId,
        propertyCode,
        roomName,
        type,
        transition,
        hotelDate,
        prefix,
      )
      events.push({
        eventId,
        propertyCode,
        roomNumber: roomName,
        type,
        at,
      })
    }

    // Stayover: in-house status becomes Stayover (once per room per hotel date, skip if room moved into)
    if (!movedToRooms.has(roomName)) {
      const prevStayover =
        typeof prevRec.inHouseStatus === 'string' &&
        /^stayover$/i.test(prevRec.inHouseStatus.trim())
      const nextStayover =
        typeof nextRec.inHouseStatus === 'string' &&
        /^stayover$/i.test(nextRec.inHouseStatus.trim())

      if (!prevStayover && nextStayover) {
        const type: PmsEventType = 'STAYOVER'
        const transition = `STAYOVER:${hotelDate}`
        const eventId = deterministicPmsEventId(
          connectionId,
          propertyCode,
          roomName,
          type,
          transition,
          hotelDate,
          prefix,
        )
        events.push({
          eventId,
          propertyCode,
          roomNumber: roomName,
          type,
          at,
        })
      }
    }
  }

  return events
}

/**
 * Pure diff between previous and next eZee room snapshots per SO-P126.
 * Thin wrapper around diffPmsRoomSnapshots with prefix 'ezee'.
 */
export function diffEzeeSnapshots(
  prev: Record<string, ReducedEzeeRoomRecord> | null | undefined,
  next: Record<string, ReducedEzeeRoomRecord>,
  options: DiffEzeeSnapshotsOptions,
): ReducedPmsEvent[] {
  return diffPmsRoomSnapshots(prev, next, { ...options, prefix: options.prefix ?? 'ezee' })
}

// ---------------------------------------------------------------------------
// Hotelogix Polling Adapter Envelope, Response Classification & Edge Reduction (SO-P127)
// ---------------------------------------------------------------------------

export interface HotelogixRequestEnvelope<T = unknown> {
  hotelogix: {
    version: string
    datetime: string
    request: {
      method: string
      key: string
      data: T
    }
  }
}

export interface BuildHotelogixEnvelopeOptions {
  version?: string
  datetime?: string
  now?: Date | number
}

/**
 * Formats a timestamp into Hotelogix GMT datetime string: YYYY-MM-DD HH:MM:SS.
 */
export function formatHotelogixGmtDatetime(date: Date | number = new Date()): string {
  const d = typeof date === 'number' ? new Date(date) : date
  const pad = (n: number) => String(n).padStart(2, '0')
  const Y = d.getUTCFullYear()
  const M = pad(d.getUTCMonth() + 1)
  const D = pad(d.getUTCDate())
  const h = pad(d.getUTCHours())
  const m = pad(d.getUTCMinutes())
  const s = pad(d.getUTCSeconds())
  return `${Y}-${M}-${D} ${h}:${m}:${s}`
}

/**
 * Builds the standard Hotelogix HAPI v1.0 JSON request envelope per SO-P127.
 */
export function buildHotelogixEnvelope<T = Record<string, unknown>>(
  method: string,
  key: string,
  data: T,
  options?: BuildHotelogixEnvelopeOptions,
): HotelogixRequestEnvelope<T> {
  return {
    hotelogix: {
      version: options?.version ?? '1.0',
      datetime: options?.datetime ?? formatHotelogixGmtDatetime(options?.now ?? new Date()),
      request: {
        method,
        key,
        data,
      },
    },
  }
}

export interface HotelogixClassifiedResponse {
  isError: boolean
  isAuth: boolean
  isMaintenance: boolean
  code: number | string | null
  message: string
}

/**
 * Classifies a Hotelogix API response per SO-P127.
 * 0 = success.
 * 1900 = invalid key / signature / consumer -> isAuth.
 * 8086 = server maintenance -> isMaintenance.
 * Any other non-zero code is an error.
 */
export function classifyHotelogixResponse(rawJson: unknown): HotelogixClassifiedResponse {
  if (!rawJson || typeof rawJson !== 'object') {
    return {
      isError: true,
      isAuth: false,
      isMaintenance: false,
      code: null,
      message: 'Invalid or non-object response from Hotelogix API',
    }
  }

  const rec = rawJson as Record<string, unknown>
  const hotelogix = (rec.hotelogix && typeof rec.hotelogix === 'object')
    ? (rec.hotelogix as Record<string, unknown>)
    : rec
  const response = (hotelogix.response && typeof hotelogix.response === 'object')
    ? (hotelogix.response as Record<string, unknown>)
    : hotelogix
  const status = (response.status && typeof response.status === 'object')
    ? (response.status as Record<string, unknown>)
    : null

  if (!status) {
    return {
      isError: false,
      isAuth: false,
      isMaintenance: false,
      code: 0,
      message: '',
    }
  }

  const rawCode = status.code
  const rawMsg = status.message ?? status.msg ?? ''
  const message = String(rawMsg).trim()

  let numCode: number | null = null
  if (typeof rawCode === 'number') {
    numCode = rawCode
  } else if (typeof rawCode === 'string' && rawCode.trim() !== '') {
    const parsed = Number(rawCode)
    if (!Number.isNaN(parsed)) {
      numCode = parsed
    }
  }

  const isAuth = numCode === 1900 || rawCode === '1900' || /(invalid key|invalid signature|invalid consumer)/i.test(message)
  const isMaintenance = numCode === 8086 || rawCode === '8086' || /maintenance/i.test(message)
  const isError = (numCode !== null && numCode !== 0) || Boolean(rawCode && rawCode !== '0')

  return {
    isError,
    isAuth,
    isMaintenance,
    code: numCode ?? (typeof rawCode === 'string' ? rawCode : null),
    message,
  }
}

export interface ReduceHotelogixStateOptions {
  hotelDate: string
  resvKeyFn: (bookingId: string) => string
}

/**
 * Pure edge reduction of raw Hotelogix API responses into allowlisted per-room records per SO-P127.
 * STRICTLY DROPS all guest personal data (names, phones, emails, addresses, rates, amounts, folios,
 * and raw booking IDs). resvKey is computed using the supplied HMAC function.
 */
export function reduceHotelogixState(
  rawHouseStatus: unknown,
  rawInHouseBookings: unknown,
  rawCheckouts: unknown,
  rawDnrs: unknown,
  options: ReduceHotelogixStateOptions,
): Record<string, ReducedHotelogixRoomRecord> {
  const { hotelDate, resvKeyFn } = options

  // Helper to unwrap { hotelogix: { response: { data } } } or direct data
  const extractData = (raw: unknown): Record<string, unknown> | null => {
    if (!raw || typeof raw !== 'object') return null
    const rec = raw as Record<string, unknown>
    const hx = (rec.hotelogix && typeof rec.hotelogix === 'object') ? (rec.hotelogix as Record<string, unknown>) : rec
    const resp = (hx.response && typeof hx.response === 'object') ? (hx.response as Record<string, unknown>) : hx
    return (resp.data && typeof resp.data === 'object') ? (resp.data as Record<string, unknown>) : resp
  }

  // 1. Extract rooms from gethousestatus
  interface RoomMeta {
    roomId: string
    roomName: string
    roomTypeId: string
  }
  const roomsByName = new Map<string, RoomMeta>()

  const houseData = extractData(rawHouseStatus)
  const roomTypesRaw = Array.isArray(houseData?.roomTypes)
    ? houseData.roomTypes
    : Array.isArray(houseData?.roomtypes)
      ? houseData.roomtypes
      : []

  for (const rt of roomTypesRaw) {
    if (!rt || typeof rt !== 'object') continue
    const rtObj = rt as Record<string, unknown>
    const roomTypeId = String(rtObj.id ?? rtObj.roomTypeId ?? '').trim()

    const unassig = Array.isArray(rtObj.unassigRooms) ? rtObj.unassigRooms : []
    const dnrRooms = Array.isArray(rtObj.dnrRooms) ? rtObj.dnrRooms : []
    const assigned = Array.isArray(rtObj.assignedRooms) ? rtObj.assignedRooms : []
    const otherRooms = Array.isArray(rtObj.rooms) ? rtObj.rooms : []

    const allTypeRooms = [...unassig, ...dnrRooms, ...assigned, ...otherRooms]
    for (const r of allTypeRooms) {
      if (!r || typeof r !== 'object') continue
      const rObj = r as Record<string, unknown>
      const roomName = String(rObj.name ?? rObj.roomName ?? rObj.id ?? '').trim()
      const roomId = String(rObj.id ?? rObj.roomId ?? roomName).trim()
      if (!roomName) continue

      const meta: RoomMeta = { roomId, roomName, roomTypeId }
      roomsByName.set(roomName.toLowerCase(), meta)
    }
  }

  // 2. Extract active DNRs from getdnrs
  const dnrData = extractData(rawDnrs)
  const dnrsRaw = Array.isArray(dnrData?.dnrs)
    ? dnrData.dnrs
    : Array.isArray(rawDnrs)
      ? rawDnrs
      : []

  interface DnrMeta {
    id: string
    isHouseUse: boolean
  }
  const dnrByRoomKey = new Map<string, DnrMeta>()

  for (const d of dnrsRaw) {
    if (!d || typeof d !== 'object') continue
    const dObj = d as Record<string, unknown>
    const id = String(dObj.id ?? '').trim()
    const roomId = String(dObj.roomId ?? dObj.roomid ?? '').trim().toLowerCase()
    const isHouseUse =
      dObj.isHouseUse === true ||
      dObj.isHouseUse === 1 ||
      dObj.isHouseUse === '1' ||
      (typeof dObj.isHouseUse === 'string' && /^(true|yes|y)$/i.test(dObj.isHouseUse.trim())) ||
      dObj.dnrType === 'HOUSEUSE'

    // SO-P127: house-use ignored
    if (roomId && id && !isHouseUse) {
      dnrByRoomKey.set(roomId, { id, isHouseUse: false })
    }
  }

  // 3. Extract in-house bookings from getbookings
  const bkgData = extractData(rawInHouseBookings)
  const bookingsRaw = Array.isArray(bkgData?.bookings)
    ? bkgData.bookings
    : Array.isArray(rawInHouseBookings)
      ? rawInHouseBookings
      : []

  interface BookingInfo {
    resvKey: string
    inHouseStatus: string
  }
  const inHouseByRoomKey = new Map<string, BookingInfo>()

  for (const b of bookingsRaw) {
    if (!b || typeof b !== 'object') continue
    const bObj = b as Record<string, unknown>
    const status = String(bObj.reservationStatus ?? bObj.status ?? '').trim().toUpperCase()
    if (status !== 'CHECKIN') continue

    const bkgId = String(bObj.id ?? bObj.bookingId ?? '').trim()
    if (!bkgId) continue

    const staysRaw = Array.isArray(bObj.roomStays) ? bObj.roomStays : []
    // Find roomStays entry matching hotelDate
    const matchingStay = staysRaw.find((s) => {
      if (!s || typeof s !== 'object') return false
      const sObj = s as Record<string, unknown>
      const sDate = String(sObj.date ?? '').slice(0, 10)
      return sDate === hotelDate
    }) as Record<string, unknown> | undefined

    if (!matchingStay) continue

    const stayRoomName = String(matchingStay.roomName ?? matchingStay.roomname ?? '').trim().toLowerCase()
    const stayRoomId = String(matchingStay.roomId ?? matchingStay.roomid ?? '').trim().toLowerCase()

    // Checkout date determination
    const stayCheckOut = String(matchingStay.checkOutDate ?? matchingStay.toDate ?? bObj.checkOutDate ?? bObj.toDate ?? '').slice(0, 10)
    let inHouseStatus = 'Stayover'
    if (stayCheckOut) {
      if (stayCheckOut > hotelDate) {
        inHouseStatus = 'Stayover'
      } else if (stayCheckOut === hotelDate) {
        inHouseStatus = 'Due Out'
      }
    }

    const resvKey = resvKeyFn(bkgId)
    const info: BookingInfo = { resvKey, inHouseStatus }

    if (stayRoomName) inHouseByRoomKey.set(stayRoomName, info)
    if (stayRoomId) inHouseByRoomKey.set(stayRoomId, info)
  }

  // 4. Extract checkouts from getcheckouts
  const coData = extractData(rawCheckouts)
  const checkoutsRaw = Array.isArray(coData?.bookings)
    ? coData.bookings
    : Array.isArray(coData?.checkouts)
      ? coData.checkouts
      : Array.isArray(rawCheckouts)
        ? rawCheckouts
        : []

  const checkoutsByRoomKey = new Set<string>()
  for (const co of checkoutsRaw) {
    if (!co || typeof co !== 'object') continue
    const coObj = co as Record<string, unknown>
    const status = String(coObj.reservationStatus ?? coObj.status ?? '').trim().toUpperCase()
    if (status !== 'CHECKOUT' && status !== 'CHECK OUT') continue

    const staysRaw = Array.isArray(coObj.roomStays) ? coObj.roomStays : []
    const matchingStay = staysRaw.find((s) => {
      if (!s || typeof s !== 'object') return false
      const sObj = s as Record<string, unknown>
      const sDate = String(sObj.date ?? '').slice(0, 10)
      return sDate === hotelDate || !sDate
    }) as Record<string, unknown> | undefined

    const rName = String(matchingStay?.roomName ?? coObj.roomName ?? coObj.room ?? '').trim().toLowerCase()
    const rId = String(matchingStay?.roomId ?? coObj.roomId ?? coObj.roomid ?? '').trim().toLowerCase()
    if (rName) checkoutsByRoomKey.add(rName)
    if (rId) checkoutsByRoomKey.add(rId)
  }

  // 5. Construct the final reduced room map
  const result: Record<string, ReducedHotelogixRoomRecord> = {}

  for (const [rNameLower, meta] of roomsByName) {
    const dnr = dnrByRoomKey.get(rNameLower) || dnrByRoomKey.get(meta.roomId.toLowerCase())
    const isBlocked = Boolean(dnr)
    const dnrId = dnr ? dnr.id : null

    const bkg = inHouseByRoomKey.get(rNameLower) || inHouseByRoomKey.get(meta.roomId.toLowerCase())
    const isCheckout = checkoutsByRoomKey.has(rNameLower) || checkoutsByRoomKey.has(meta.roomId.toLowerCase())

    let inHouseStatus: string | null = null
    let resvKey: string | null = null

    if (bkg) {
      inHouseStatus = bkg.inHouseStatus
      resvKey = bkg.resvKey
    } else if (isCheckout) {
      inHouseStatus = 'Check Out'
      resvKey = null
    }

    result[meta.roomName] = {
      roomName: meta.roomName,
      roomId: meta.roomId,
      roomTypeId: meta.roomTypeId,
      isBlocked,
      hkStatus: '',
      roomStatus: '',
      inHouseStatus,
      resvKey,
      dnrId,
    }
  }

  return result
}

// ---------------------------------------------------------------------------
// Report Upload Adapters (Stayflexi SO-P128, Djubo SO-P270, IDS Next SO-P271)
// ---------------------------------------------------------------------------

export type ReportUploadAdapter = 'STAYFLEXI' | 'DJUBO' | 'IDS_NEXT'

export interface ReducedStayflexiRow {
  roomName: string
  occupancy: 'OCCUPIED' | 'VACANT' | null
  hkStatus: 'CLEAN' | 'DIRTY' | 'INSPECTED' | null
  blocked: boolean
  dueOut: boolean
  stayover: boolean
}

export type ReducedPmsReportRow = ReducedStayflexiRow

export interface StayflexiColumnMap {
  room?: string
  status?: string
  occupancy?: string
  blocked?: string
  dueOut?: string
  stayover?: string
}

export type PmsReportColumnMap = StayflexiColumnMap

export const STAYFLEXI_HEADER_SYNONYMS: Record<keyof StayflexiColumnMap, RegExp> = {
  room: /^(room|room\s*no\.?|room\s*number|room\s*name|room\s*#|room_number|room_no)$/i,
  status: /^(status|room\s*status|housekeeping\s*status|hk\s*status|hk_status|room_status)$/i,
  occupancy: /^(occupancy|occupied|vacant|guest\s*status|occ\s*\/?\s*vac|occupancy\s*status)$/i,
  blocked: /^(blocked|out\s*of\s*order|ooo|maintenance|out_of_order|is_blocked)$/i,
  dueOut: /^(due\s*out|departure\s*today|dep\s*today|departing\s*today|due_out)$/i,
  stayover: /^(stayover|stay\s*over|is_stayover)$/i,
}

export const DJUBO_HEADER_SYNONYMS: Record<keyof StayflexiColumnMap, RegExp> = {
  room: /^(room|room\s*no\.?|room\s*number|room\s*name|room\s*#|room_number|room_no)$/i,
  status: /^(status|room\s*status|housekeeping\s*status|housekeeping|hk\s*status|hk_status|room_status)$/i,
  occupancy: /^(occupancy|occupied|vacant|guest\s*status|occ\s*\/?\s*vac|occupancy\s*status)$/i,
  blocked: /^(blocked|out\s*of\s*order|ooo|maintenance|out_of_order|is_blocked)$/i,
  dueOut: /^(due\s*out|departure|departure\s*today|dep\s*today|departing\s*today|due_out)$/i,
  stayover: /^(stayover|stay\s*over|is_stayover)$/i,
}

export const IDS_NEXT_HEADER_SYNONYMS: Record<keyof StayflexiColumnMap, RegExp> = {
  room: /^(room_number|room_no|room|room\s*no\.?|room\s*number|room\s*name|room\s*#)$/i,
  status: /^(status_housekeeping_cleaning|status|room\s*status|housekeeping\s*status|housekeeping|hk\s*status|hk_status|room_status)$/i,
  occupancy: /^(status_front_office_occupancy|fo\s*status|fo\s*occupancy|occupancy|occupied|vacant|guest\s*status|occ\s*\/?\s*vac|occupancy\s*status)$/i,
  blocked: /^(status_inventory|blocked|out\s*of\s*order|ooo|oos|out\s*of\s*service|maintenance|out_of_order|is_blocked)$/i,
  dueOut: /^(due_out|due\s*out|departure|departure\s*today|dep\s*today|departing\s*today)$/i,
  stayover: /^(stayover|stay\s*over|is_stayover)$/i,
}

export const REPORT_UPLOAD_HEADER_SYNONYMS: Record<ReportUploadAdapter, Record<keyof StayflexiColumnMap, RegExp>> = {
  STAYFLEXI: STAYFLEXI_HEADER_SYNONYMS,
  DJUBO: DJUBO_HEADER_SYNONYMS,
  IDS_NEXT: IDS_NEXT_HEADER_SYNONYMS,
}

export function autoDetectReportColumns(
  headers: string[],
  adapter: ReportUploadAdapter = 'STAYFLEXI',
): {
  detected: StayflexiColumnMap
  missingRequired: Array<'room' | 'status'>
} {
  const synonyms = REPORT_UPLOAD_HEADER_SYNONYMS[adapter] || STAYFLEXI_HEADER_SYNONYMS
  const detected: StayflexiColumnMap = {}
  const usedIndices = new Set<number>()

  const keys: Array<keyof StayflexiColumnMap> = ['room', 'status', 'occupancy', 'blocked', 'dueOut', 'stayover']
  for (const key of keys) {
    const rx = synonyms[key]
    for (let i = 0; i < headers.length; i++) {
      if (usedIndices.has(i)) continue
      const h = String(headers[i] ?? '').trim()
      if (rx.test(h)) {
        detected[key] = h
        usedIndices.add(i)
        break
      }
    }
  }

  const missingRequired: Array<'room' | 'status'> = []
  if (!detected.room) missingRequired.push('room')
  if (!detected.status) missingRequired.push('status')

  return { detected, missingRequired }
}

export function autoDetectStayflexiColumns(headers: string[]): {
  detected: StayflexiColumnMap
  missingRequired: Array<'room' | 'status'>
} {
  return autoDetectReportColumns(headers, 'STAYFLEXI')
}

export interface ReduceReportUploadRowsOptions {
  adapter?: ReportUploadAdapter
  columnMap?: StayflexiColumnMap
}

/**
 * Reduces raw tabular PMS report rows (Stayflexi, Djubo, IDS Next) to strictly allowlisted fields.
 * Any other columns (guest names, phones, amounts, booking IDs, notes) are dropped.
 */
export function reduceReportUploadRows(
  headers: string[],
  rows: (unknown[] | Record<string, unknown>)[],
  options?: ReduceReportUploadRowsOptions | StayflexiColumnMap,
): ReducedStayflexiRow[] {
  const adapter: ReportUploadAdapter =
    options && 'adapter' in options && options.adapter ? options.adapter : 'STAYFLEXI'
  const explicitMap: StayflexiColumnMap | undefined =
    options && 'columnMap' in options
      ? options.columnMap
      : options && !('adapter' in options)
        ? (options as StayflexiColumnMap)
        : undefined

  const normHeaders = headers.map((h) => String(h ?? '').trim())
  const { detected } = autoDetectReportColumns(normHeaders, adapter)

  const effectiveMap: StayflexiColumnMap = {
    ...detected,
    ...(explicitMap || {}),
  }

  const findColIndex = (colKey: keyof StayflexiColumnMap): number => {
    const mapped = effectiveMap[colKey]
    if (!mapped) return -1
    const mappedNorm = mapped.trim().toLowerCase()
    const idx = normHeaders.findIndex((h) => h.toLowerCase() === mappedNorm)
    if (idx >= 0) return idx
    const asNum = Number(mapped)
    if (!isNaN(asNum) && asNum >= 0 && asNum < normHeaders.length) {
      return asNum
    }
    return -1
  }

  const roomIdx = findColIndex('room')
  const statusIdx = findColIndex('status')

  if (roomIdx < 0) {
    throw new Error('Room column could not be detected. Please provide a column mapping.')
  }
  if (statusIdx < 0) {
    throw new Error('Status column could not be detected. Please provide a column mapping.')
  }

  const occIdx = findColIndex('occupancy')
  const blockedIdx = findColIndex('blocked')
  const dueOutIdx = findColIndex('dueOut')
  const stayoverIdx = findColIndex('stayover')

  const getCell = (row: unknown[] | Record<string, unknown>, idx: number, colName?: string): string => {
    if (Array.isArray(row)) {
      if (idx < 0 || idx >= row.length) return ''
      const val = row[idx]
      return val !== null && val !== undefined ? String(val).trim() : ''
    }
    if (row && typeof row === 'object') {
      if (colName && colName in row) {
        const val = (row as Record<string, unknown>)[colName]
        return val !== null && val !== undefined ? String(val).trim() : ''
      }
      if (idx >= 0 && idx < normHeaders.length) {
        const h = normHeaders[idx]
        const val = (row as Record<string, unknown>)[h]
        return val !== null && val !== undefined ? String(val).trim() : ''
      }
    }
    return ''
  }

  const result: ReducedStayflexiRow[] = []

  for (const row of rows) {
    if (!row) continue
    const roomName = getCell(row, roomIdx, effectiveMap.room)
    if (!roomName) continue // skip empty rows

    const rawStatus = getCell(row, statusIdx, effectiveMap.status)
    const rawOcc = occIdx >= 0 ? getCell(row, occIdx, effectiveMap.occupancy) : ''
    const rawBlocked = blockedIdx >= 0 ? getCell(row, blockedIdx, effectiveMap.blocked) : ''
    const rawDueOut = dueOutIdx >= 0 ? getCell(row, dueOutIdx, effectiveMap.dueOut) : ''
    const rawStayover = stayoverIdx >= 0 ? getCell(row, stayoverIdx, effectiveMap.stayover) : ''

    // Any unicode dash or hyphen between words: -, ‐, ‑, ‒, –, —, ―, −, ﹘, ﹣, －
    const UNICODE_DASH = '[-\\u2010-\\u2015\\u2212\\uFE58\\uFE63\\uFF0D]'
    const isDirtyCheckout =
      new RegExp(`^dirty\\s*${UNICODE_DASH}\\s*check\\s*out$`, 'i').test(rawStatus) ||
      /^(check\s*out|checked\s*out)$/i.test(rawStatus)
    const isDirtyStayover =
      new RegExp(`^dirty\\s*${UNICODE_DASH}\\s*stay\\s*over$`, 'i').test(rawStatus) ||
      /^stay\s*over$/i.test(rawStatus)

    // HK Status
    let hkStatus: 'CLEAN' | 'DIRTY' | 'INSPECTED' | null = null
    if (/clean/i.test(rawStatus) && !/dirty/i.test(rawStatus)) {
      hkStatus = 'CLEAN'
    } else if (/dirty/i.test(rawStatus) || isDirtyCheckout || isDirtyStayover) {
      hkStatus = 'DIRTY'
    } else if (/inspect/i.test(rawStatus)) {
      hkStatus = 'INSPECTED'
    }

    // Occupancy
    let occupancy: 'OCCUPIED' | 'VACANT' | null = null
    if (/^(occupied|occ|yes|true|1)$/i.test(rawOcc)) {
      occupancy = 'OCCUPIED'
    } else if (/^(vacant|vac|no|false|0)$/i.test(rawOcc)) {
      occupancy = 'VACANT'
    } else {
      if (isDirtyCheckout) {
        occupancy = 'VACANT'
      } else if (isDirtyStayover) {
        occupancy = 'OCCUPIED'
      }
    }

    // Blocked (OOO / OOS / Maintenance)
    let blocked = false
    if (/^(yes|true|1|blocked|ooo|oos|out\s*of\s*order|out\s*of\s*service|maintenance)$/i.test(rawBlocked)) {
      blocked = true
    } else if (
      /^(blocked|ooo|oos|out\s*of\s*order|out\s*of\s*service|maintenance)$/i.test(rawStatus) ||
      /\b(ooo|oos|out\s*of\s*order|out\s*of\s*service)\b/i.test(rawStatus)
    ) {
      blocked = true
    }

    // Due Out
    let dueOut = false
    if (/^(yes|true|1|due\s*out|departure|departure\s*today)$/i.test(rawDueOut)) {
      dueOut = true
    } else if (/\bdue\s*out\b/i.test(rawStatus)) {
      dueOut = true
    }

    // Stayover
    let stayover = false
    if (/^(yes|true|1|stayover|stay\s*over)$/i.test(rawStayover)) {
      stayover = true
    } else if (isDirtyStayover || /\bstay\s*over\b/i.test(rawStatus)) {
      stayover = true
    }

    result.push({
      roomName,
      occupancy,
      hkStatus,
      blocked,
      dueOut,
      stayover,
    })
  }

  return result
}

/**
 * Reduces raw tabular Stayflexi report rows to strictly allowlisted fields.
 * Any other columns (guest names, phones, amounts, booking IDs, notes) are dropped.
 */
export function reduceStayflexiReportRows(
  headers: string[],
  rows: (unknown[] | Record<string, unknown>)[],
  columnMap?: StayflexiColumnMap,
): ReducedStayflexiRow[] {
  return reduceReportUploadRows(headers, rows, { adapter: 'STAYFLEXI', columnMap })
}

export interface DiffReportUploadsOptions {
  connectionId: string
  propertyCode: string
  hotelDate: string
  at?: number
  adapter?: PmsAdapterType
  prefix?: string
}

export type DiffStayflexiReportsOptions = DiffReportUploadsOptions

export interface ImportStaysPmsReportInput {
  workspaceId: string
  propertyCode: string
  rows: ReducedStayflexiRow[]
  columnMap?: Record<string, string>
  opId?: string
}

export interface ImportStaysPmsReportResult {
  totalRows: number
  rows?: number
  mappedRooms: number
  unmappedRoomNames: string[]
  eventsApplied: number
  baseline: boolean
}

/**
 * Pure diff between previous and next PMS report room snapshots per SO-P128, SO-P270, SO-P271.
 *
 * Rules:
 * 1. First upload (or after reset) = baseline, returns [].
 * 2. Room absent from next upload emits nothing.
 * 3. occupancy OCCUPIED -> VACANT, or status becoming "Dirty - Checkout" -> CHECKED_OUT.
 * 4. blocked false -> true -> OUT_OF_ORDER, true -> false -> BACK_IN_SERVICE.
 * 5. dueOut false -> true -> DUE_OUT.
 * 6. stayover false -> true -> STAYOVER (once per room per hotel date).
 * 7. No ROOM_MOVE (the report has no reservation key).
 * 8. Event IDs are deterministic: <prefix>_<hash(connection, property, room, type, transition, hotelDate)>.
 *    Prefix defaults to: 'djubo' for DJUBO, 'idsnext' for IDS_NEXT, 'stayflexi' otherwise.
 */
export function diffReportUploads(
  prev: Record<string, ReducedStayflexiRow> | null | undefined,
  next: Record<string, ReducedStayflexiRow>,
  options: DiffReportUploadsOptions,
): ReducedPmsEvent[] {
  if (!prev || Object.keys(prev).length === 0) {
    return []
  }

  const { connectionId, propertyCode, hotelDate } = options
  const prefix =
    options.prefix ||
    (options.adapter === 'DJUBO'
      ? 'djubo'
      : options.adapter === 'IDS_NEXT'
        ? 'idsnext'
        : 'stayflexi')
  const at = typeof options.at === 'number' ? options.at : Date.now()
  const events: ReducedPmsEvent[] = []

  for (const [roomName, prevRec] of Object.entries(prev)) {
    const nextRec = next[roomName]
    // Absent room in next emits nothing
    if (!nextRec) {
      continue
    }

    // CHECKED_OUT: may fire only when prev occupancy === 'OCCUPIED' and next is VACANT,
    // or when prev.stayover was true and next is VACANT. Prev occupancy null never emits CHECKED_OUT.
    const nextIsVacant = nextRec.occupancy === 'VACANT'
    const isCheckout =
      nextIsVacant && (prevRec.occupancy === 'OCCUPIED' || prevRec.stayover === true)

    if (isCheckout) {
      const type: PmsEventType = 'CHECKED_OUT'
      const transition = 'CHECKED_OUT'
      const eventId = deterministicPmsEventId(
        connectionId,
        propertyCode,
        roomName,
        type,
        transition,
        hotelDate,
        prefix,
      )
      events.push({
        eventId,
        propertyCode,
        roomNumber: roomName,
        type,
        at,
      })
    }

    // Blocked: OUT_OF_ORDER / BACK_IN_SERVICE
    if (!prevRec.blocked && nextRec.blocked) {
      const type: PmsEventType = 'OUT_OF_ORDER'
      const transition = 'isBlocked:false->true'
      const eventId = deterministicPmsEventId(
        connectionId,
        propertyCode,
        roomName,
        type,
        transition,
        hotelDate,
        prefix,
      )
      events.push({
        eventId,
        propertyCode,
        roomNumber: roomName,
        type,
        at,
      })
    } else if (prevRec.blocked && !nextRec.blocked) {
      const type: PmsEventType = 'BACK_IN_SERVICE'
      const transition = 'isBlocked:true->false'
      const eventId = deterministicPmsEventId(
        connectionId,
        propertyCode,
        roomName,
        type,
        transition,
        hotelDate,
        prefix,
      )
      events.push({
        eventId,
        propertyCode,
        roomNumber: roomName,
        type,
        at,
      })
    }

    // DUE_OUT
    if (!prevRec.dueOut && nextRec.dueOut) {
      const type: PmsEventType = 'DUE_OUT'
      const transition = 'dueOut:false->true'
      const eventId = deterministicPmsEventId(
        connectionId,
        propertyCode,
        roomName,
        type,
        transition,
        hotelDate,
        prefix,
      )
      events.push({
        eventId,
        propertyCode,
        roomNumber: roomName,
        type,
        at,
      })
    }

    // STAYOVER (once per room per hotel date)
    if (!prevRec.stayover && nextRec.stayover) {
      const type: PmsEventType = 'STAYOVER'
      const transition = `STAYOVER:${hotelDate}`
      const eventId = deterministicPmsEventId(
        connectionId,
        propertyCode,
        roomName,
        type,
        transition,
        hotelDate,
        prefix,
      )
      events.push({
        eventId,
        propertyCode,
        roomNumber: roomName,
        type,
        at,
      })
    }
  }

  return events
}

/**
 * Pure diff between previous and next Stayflexi report room snapshots per SO-P128.
 */
export function diffStayflexiReports(
  prev: Record<string, ReducedStayflexiRow> | null | undefined,
  next: Record<string, ReducedStayflexiRow>,
  options: DiffStayflexiReportsOptions,
): ReducedPmsEvent[] {
  return diffReportUploads(prev, next, { ...options, adapter: options.adapter || 'STAYFLEXI' })
}
